/**
 * Extracts product information from a product object, including variant handling,
 * store information, prices, and images.
 * 
 * @param {Object} product - The product object
 * @param {string} storeId - The store ID to get information for (default: "0")
 * @returns {Object} An object containing:
 *   - firstVariantOrProduct: The first variant or the product itself
 *   - infoStore: Store-specific information
 *   - pricesByType: A Map of prices by type
 *   - normalPrice: The normal price value
 *   - mainImage: The main image thumbnail URL
 */
export function getProductInfo(product, storeIds = ["0"]) {
  if (!product) {
    return {
      firstVariantOrProduct: null,
      infoStore: null,
      pricesByType: new Map(),
      normalPrice: null,
      salePrice: null,
      exclusivePrice: null,
      commonPrice: null,
      mainImage: null,
      discountAmount: null,
      discountPercentage: null,
      exclusiveDiscountAmount: null,
      exclusiveDiscountPercentage: null,
    };
  }

  const firstVariantOrProduct = product.variants?.[0] || product;
  // first storeId in storeIds
  let firstFoundInfoStore;
  storeIds.find((storeId) => {
    if (firstVariantOrProduct.info_stores?.[storeId]) {
      firstFoundInfoStore = storeId;
      if (firstVariantOrProduct.info_stores[storeId].is_available === true && firstVariantOrProduct.info_stores[storeId].max_per_item > 0 && firstVariantOrProduct.info_stores[storeId].prices && firstVariantOrProduct.info_stores[storeId].prices.length > 0) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  });
  const infoStore = firstVariantOrProduct.info_stores?.[firstFoundInfoStore];
  
  const pricesByType = (infoStore?.prices || []).reduce((acc, currentPrice) => {
    acc.set(currentPrice.type, currentPrice);
    return acc;
  }, new Map());

  const normalPrice = pricesByType.get("normal")?.value;
  const salePrice = pricesByType.get("sale")?.value;
  const exclusivePrice = pricesByType.get("exclusive")?.value;
  const commonPrice = salePrice || normalPrice;
  const mainImage = firstVariantOrProduct?.multimedia?.main_image?.thumbnail;
  const discountAmount = normalPrice > commonPrice ? normalPrice - commonPrice : 0;
  const discountPercentage = (discountAmount / normalPrice) * 100;
  const exclusiveDiscountAmount = normalPrice > exclusivePrice ? normalPrice - exclusivePrice : 0;
  const exclusiveDiscountPercentage = (exclusiveDiscountAmount / normalPrice) * 100;

  return {
    firstVariantOrProduct,
    infoStore,
    pricesByType,
    salePrice,
    normalPrice,
    exclusivePrice,
    commonPrice,
    mainImage,
    discountAmount,
    discountPercentage,
    exclusiveDiscountAmount,
    exclusiveDiscountPercentage,
  };
}

/**
 * Whether a product is sold to adults only (+18, e.g. alcohol). Same rule as kiosk-backend's pickup
 * ticket: the item's `age_restricted` flag, or the legacy string tag "alcohol". Tags shaped as maps
 * come from product features and don't count, so "alcohol gel" in a hardware store isn't flagged.
 *
 * @param {Object} product - The product object
 * @returns {boolean}
 */
export function isAgeRestricted(product) {
  if (!product) return false;
  if (product.age_restricted === true) return true;
  return Array.isArray(product.tags) &&
    product.tags.some((tag) => typeof tag === 'string' && tag.toLowerCase() === 'alcohol');
}
